/**
 * Enhanced API Client with Performance Optimization and Caching
 * 
 * Integrates:
 * - Advanced caching strategies
 * - Request deduplication
 * - Performance monitoring
 * - Automatic optimization
 * - Batch request handling
 */

import { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { cacheManager as defaultCacheManager, cacheConfigs, CacheManager } from './cache-manager';
import { performanceOptimizer } from './performance-optimizer';
import { withRetry, retryConfigs } from '../retryMechanism';
import apiClient from './client';

export interface EnhancedRequestConfig extends AxiosRequestConfig {
  // Caching options
  enableCaching?: boolean;
  cacheKey?: string;
  cacheTTL?: number;
  cacheTags?: string[];
  cacheStrategy?: 'memory' | 'storage' | 'hybrid';
  
  // Performance options
  enableMetrics?: boolean;
  enableBatching?: boolean;
  batchKey?: string;
  debounceDelay?: number;
  
  // Retry options
  retryConfig?: 'standard' | 'critical' | 'fast' | 'upload';
  customRetryConfig?: {
    maxRetries?: number;
    delay?: number;
    backoff?: 'fixed' | 'exponential';
    retryCondition?: (error: AxiosError) => boolean;
  };
  
  // Optimization options
  enableOptimization?: boolean;
  priority?: 'low' | 'medium' | 'high' | 'critical';
  timeout?: number;
}

export interface RequestInterceptor {
  name: string;
  priority: number;
  onRequest?: (config: EnhancedRequestConfig) => Promise<EnhancedRequestConfig>;
  onResponse?: (response: AxiosResponse) => Promise<AxiosResponse>;
  onError?: (error: AxiosError) => Promise<AxiosError>;
}

export class EnhancedApiClient {
  private axiosInstance: AxiosInstance;
  private cacheManager: CacheManager;
  private interceptors: RequestInterceptor[] = [];
  private requestQueue = new Map<string, Promise<AxiosResponse<unknown>>>();
  private config: {
    enableGlobalCaching: boolean;
    enableGlobalMetrics: boolean;
    enableGlobalOptimization: boolean;
    defaultCacheTTL: number;
    maxConcurrentRequests: number;
    requestQueueTimeout: number;
  };

  constructor(
    baseAxiosInstance?: AxiosInstance,
    cacheManager?: CacheManager,
    config: Partial<EnhancedApiClient['config']> = {}
  ) {
    this.axiosInstance = baseAxiosInstance || apiClient;
    this.cacheManager = cacheManager || defaultCacheManager;
    
    this.config = {
      enableGlobalCaching: true,
      enableGlobalMetrics: true,
      enableGlobalOptimization: true,
      defaultCacheTTL: 5 * 60 * 1000, // 5 minutes
      maxConcurrentRequests: 10,
      requestQueueTimeout: 30000,
      ...config,
    };

    this.setupDefaultInterceptors();
  }

  /**
   * Enhanced GET request with caching and optimization
   */
  async get<T = unknown>(url: string, config: EnhancedRequestConfig = {}): Promise<AxiosResponse<T>> {
    const enhancedConfig = this.mergeConfig('GET', url, config);
    return this.executeRequest<T>(() => this.axiosInstance.get<T>(url, enhancedConfig), enhancedConfig);
  }

  /**
   * Enhanced POST request with optimization
   */
  async post<T = unknown>(url: string, data?: unknown, config: EnhancedRequestConfig = {}): Promise<AxiosResponse<T>> {
    const enhancedConfig = this.mergeConfig('POST', url, config);
    return this.executeRequest<T>(() => this.axiosInstance.post<T>(url, data, enhancedConfig), enhancedConfig);
  }

  /**
   * Enhanced PUT request with optimization
   */
  async put<T = unknown>(url: string, data?: unknown, config: EnhancedRequestConfig = {}): Promise<AxiosResponse<T>> {
    const enhancedConfig = this.mergeConfig('PUT', url, config);
    return this.executeRequest<T>(() => this.axiosInstance.put<T>(url, data, enhancedConfig), enhancedConfig);
  }

  /**
   * Enhanced PATCH request with optimization
   */
  async patch<T = unknown>(url: string, data?: unknown, config: EnhancedRequestConfig = {}): Promise<AxiosResponse<T>> {
    const enhancedConfig = this.mergeConfig('PATCH', url, config);
    return this.executeRequest<T>(() => this.axiosInstance.patch<T>(url, data, enhancedConfig), enhancedConfig);
  }

  /**
   * Enhanced DELETE request with optimization
   */
  async delete<T = unknown>(url: string, config: EnhancedRequestConfig = {}): Promise<AxiosResponse<T>> {
    const enhancedConfig = this.mergeConfig('DELETE', url, config);
    return this.executeRequest<T>(() => this.axiosInstance.delete<T>(url, enhancedConfig), enhancedConfig);
  }

  /**
   * Batch multiple requests together
   */
  async batch<T = unknown>(requests: Array<{
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    url: string;
    data?: unknown;
    config?: EnhancedRequestConfig;
  }>): Promise<AxiosResponse<T>[]> {
    const batchKey = `batch-${Date.now()}-${Math.random()}`;
    
    return Promise.all(
      requests.map(({ method, url, data, config = {} }) => {
        const enhancedConfig = { ...config, enableBatching: true, batchKey };
        
        switch (method) {
          case 'GET':
            return this.get<T>(url, enhancedConfig);
          case 'POST':
            return this.post<T>(url, data, enhancedConfig);
          case 'PUT':
            return this.put<T>(url, data, enhancedConfig);
          case 'PATCH':
            return this.patch<T>(url, data, enhancedConfig);
          case 'DELETE':
            return this.delete<T>(url, enhancedConfig);
          default:
            throw new Error(`Unsupported method: ${method}`);
        }
      })
    );
  }

  /**
   * Cached request with automatic cache key generation
   */
  async cachedRequest<T = unknown>(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    url: string,
    data?: unknown,
    cacheOptions: {
      ttl?: number;
      tags?: string[];
      forceRefresh?: boolean;
      cacheStrategy?: 'memory' | 'storage' | 'hybrid';
    } = {}
  ): Promise<T> {
    const cacheKey = this.generateCacheKey(method, url, data);
    const config: EnhancedRequestConfig = {
      enableCaching: true,
      cacheKey,
      cacheTTL: cacheOptions.ttl,
      cacheTags: cacheOptions.tags,
      cacheStrategy: cacheOptions.cacheStrategy,
    };

    if (cacheOptions.forceRefresh) {
      await this.cacheManager.delete(cacheKey);
    }

    const response = await this.executeRequest<T>(
      () => {
        switch (method) {
          case 'GET':
            return this.axiosInstance.get<T>(url, config);
          case 'POST':
            return this.axiosInstance.post<T>(url, data, config);
          case 'PUT':
            return this.axiosInstance.put<T>(url, data, config);
          case 'PATCH':
            return this.axiosInstance.patch<T>(url, data, config);
          case 'DELETE':
            return this.axiosInstance.delete<T>(url, config);
          default:
            throw new Error(`Unsupported method: ${method}`);
        }
      },
      config
    );

    return response.data;
  }

  /**
   * Prefetch data and store in cache
   */
  async prefetch<T = unknown>(
    method: 'GET' | 'POST',
    url: string,
    data?: unknown,
    cacheOptions: {
      ttl?: number;
      tags?: string[];
      priority?: 'low' | 'medium' | 'high';
    } = {}
  ): Promise<void> {
    const config: EnhancedRequestConfig = {
      enableCaching: true,
      cacheKey: this.generateCacheKey(method, url, data),
      cacheTTL: cacheOptions.ttl || this.config.defaultCacheTTL,
      cacheTags: cacheOptions.tags,
      priority: cacheOptions.priority || 'low',
    };

    try {
      await this.executeRequest<T>(
        () => method === 'GET' 
          ? this.axiosInstance.get<T>(url, config)
          : this.axiosInstance.post<T>(url, data, config),
        config
      );
    } catch (error) {
      // Prefetch failures shouldn't break the application
      console.warn('Prefetch failed:', error);
    }
  }

  /**
   * Add request interceptor
   */
  addInterceptor(interceptor: RequestInterceptor): void {
    this.interceptors.push(interceptor);
    this.interceptors.sort((a, b) => b.priority - a.priority);
  }

  /**
   * Remove request interceptor
   */
  removeInterceptor(name: string): void {
    this.interceptors = this.interceptors.filter(i => i.name !== name);
  }

  /**
   * Get performance metrics
   */
  getPerformanceMetrics() {
    return performanceOptimizer.generatePerformanceReport();
  }

  /**
   * Get cache statistics
   */
  getCacheStatistics() {
    if (!this.cacheManager || typeof this.cacheManager.getMetrics !== 'function') {
      return {
        hits: 0,
        misses: 0,
        sets: 0,
        deletes: 0,
        evictions: 0,
        hitRate: 0,
        avgResponseTime: 0,
        totalSize: 0,
        entryCount: 0,
      };
    }
    return this.cacheManager.getMetrics();
  }

  /**
   * Clear all caches
   */
  async clearCache(pattern?: string | RegExp): Promise<void> {
    if (pattern) {
      await this.cacheManager.invalidate(pattern);
    } else {
      await this.cacheManager.clear();
    }
  }

  /**
   * Configure the client
   */
  configure(newConfig: Partial<EnhancedApiClient['config']>): void {
    this.config = { ...this.config, ...newConfig };
  }

  // Private methods

  private async executeRequest<T>(
    requestFn: () => Promise<AxiosResponse<T>>,
    config: EnhancedRequestConfig
  ): Promise<AxiosResponse<T>> {
    // Apply request interceptors
    let processedConfig = config;
    for (const interceptor of this.interceptors) {
      if (interceptor.onRequest) {
        processedConfig = await interceptor.onRequest(processedConfig);
      }
    }

    // Handle deduplication
    const deduplicationKey = this.generateDeduplicationKey(processedConfig);
    if (this.requestQueue.has(deduplicationKey)) {
      return this.requestQueue.get(deduplicationKey)! as Promise<AxiosResponse<T>>;
    }

    // Create request promise
    const requestPromise = this.executeWithOptimization(requestFn, processedConfig);
    
    // Store in queue for deduplication
    this.requestQueue.set(deduplicationKey, requestPromise);
    
    try {
      const response = await requestPromise;
      
      // Apply response interceptors
      let processedResponse = response;
      for (const interceptor of this.interceptors) {
        if (interceptor.onResponse) {
          processedResponse = await interceptor.onResponse(processedResponse);
        }
      }
      
      return processedResponse;
    } catch (error) {
      // Apply error interceptors
      let processedError = error as AxiosError;
      for (const interceptor of this.interceptors) {
        if (interceptor.onError) {
          processedError = await interceptor.onError(processedError);
        }
      }
      
      throw processedError;
    } finally {
      // Clean up from queue
      setTimeout(() => {
        this.requestQueue.delete(deduplicationKey);
      }, 1000); // Keep for 1 second to handle rapid duplicates
    }
  }

  private async executeWithOptimization<T>(
    requestFn: () => Promise<AxiosResponse<T>>,
    config: EnhancedRequestConfig
  ): Promise<AxiosResponse<T>> {
    const { 
      enableCaching = this.config.enableGlobalCaching,
      enableMetrics = this.config.enableGlobalMetrics,
      enableOptimization = this.config.enableGlobalOptimization,
      retryConfig = 'standard',
      customRetryConfig,
      debounceDelay,
      cacheKey,
      cacheTTL,
      cacheTags,
    } = config;

    const url = this.extractUrlFromConfig(config);
    const method = this.extractMethodFromConfig(config);

    // Handle debouncing
    if (debounceDelay && debounceDelay > 0) {
      return performanceOptimizer.debounceRequest(
        `${method}:${url}`,
        requestFn,
        debounceDelay
      );
    }

    // Create the optimized request function
    const optimizedRequestFn = async (): Promise<AxiosResponse<T>> => {
      // Apply retry logic
      const retryConfigToUse = customRetryConfig || retryConfigs[retryConfig as keyof typeof retryConfigs];
      return withRetry(requestFn, retryConfigToUse);
    };

    // Apply performance monitoring and caching
    if (enableMetrics && enableOptimization) {
      return performanceOptimizer.measureRequest(
        url,
        method,
        optimizedRequestFn,
        {
          enableCaching,
          cacheKey,
          cacheTTL,
          tags: cacheTags,
        }
      );
    }

    // Apply caching without metrics
    if (enableCaching && cacheKey) {
      return this.cacheManager.deduplicate(
        cacheKey,
        optimizedRequestFn,
        {
          ttl: cacheTTL,
          tags: cacheTags,
        }
      );
    }

    // Execute request without optimizations
    return optimizedRequestFn();
  }

  private mergeConfig(method: string, url: string, config: EnhancedRequestConfig): EnhancedRequestConfig {
    const defaultConfig: EnhancedRequestConfig = {
      enableCaching: this.shouldEnableDefaultCaching(method, url),
      enableMetrics: this.config.enableGlobalMetrics,
      enableOptimization: this.config.enableGlobalOptimization,
      retryConfig: 'standard',
      priority: 'medium',
    };

    const mergedConfig = { ...defaultConfig, ...config };

    // Auto-generate cache key if caching is enabled but no key provided
    if (mergedConfig.enableCaching && !mergedConfig.cacheKey) {
      mergedConfig.cacheKey = this.generateCacheKey(method, url, config.data);
    }

    // Auto-set cache TTL based on endpoint type if not provided
    if (mergedConfig.enableCaching && !mergedConfig.cacheTTL) {
      mergedConfig.cacheTTL = this.suggestCacheTTL(url);
    }

    // Auto-set cache tags based on URL patterns
    if (mergedConfig.enableCaching && !mergedConfig.cacheTags) {
      mergedConfig.cacheTags = this.suggestCacheTags(url);
    }

    return mergedConfig;
  }

  private shouldEnableDefaultCaching(method: string, url: string): boolean {
    // Only cache GET requests by default
    if (method !== 'GET') return false;
    
    // Don't cache real-time endpoints
    if (url.includes('/realtime') || url.includes('/live')) return false;
    
    // Don't cache user-specific endpoints that change frequently
    if (url.includes('/me/notifications') || url.includes('/me/alerts')) return false;
    
    return this.config.enableGlobalCaching;
  }

  private suggestCacheTTL(url: string): number {
    // Reference data - cache for longer
    if (url.includes('/reference') || url.includes('/lookup')) {
      return cacheConfigs.longTerm.ttl;
    }
    
    // User profile data
    if (url.includes('/users/me') || url.includes('/profile')) {
      return cacheConfigs.userSpecific.ttl;
    }
    
    // Establishment data
    if (url.includes('/establishments')) {
      return cacheConfigs.establishmentSpecific.ttl;
    }
    
    // Search results
    if (url.includes('/search') || url.includes('?search=')) {
      return cacheConfigs.searchResults.ttl;
    }
    
    // Real-time data
    if (url.includes('/notifications') || url.includes('/alerts')) {
      return cacheConfigs.realTime.ttl;
    }
    
    return this.config.defaultCacheTTL;
  }

  private suggestCacheTags(url: string): string[] {
    const tags: string[] = [];
    
    if (url.includes('/users')) tags.push('users');
    if (url.includes('/establishments')) tags.push('establishments');
    if (url.includes('/maintenance')) tags.push('maintenance');
    if (url.includes('/documents')) tags.push('documents');
    if (url.includes('/communications')) tags.push('communications');
    if (url.includes('/search')) tags.push('search');
    if (url.includes('/me/')) tags.push('user-specific');
    
    return tags;
  }

  private generateCacheKey(method: string, url: string, data?: unknown): string {
    if (!this.cacheManager || typeof this.cacheManager.generateKey !== 'function') {
      // Fallback cache key generation
      const establishmentId = localStorage.getItem('mep_current_establishment') || '';
      const userId = this.getUserIdFromToken() || '';
      const params = data ? JSON.stringify(data) : '';
      return `${method}:${url}:${params}:${establishmentId}:${userId}`;
    }

    const establishmentId = localStorage.getItem('mep_current_establishment') || '';
    const userId = this.getUserIdFromToken() || '';
    
    return this.cacheManager.generateKey({
      method,
      url,
      params: data,
      establishmentId,
      headers: { 'user-id': userId },
    } as Parameters<CacheManager['generateKey']>[0]);
  }

  private generateDeduplicationKey(config: EnhancedRequestConfig): string {
    const method = this.extractMethodFromConfig(config);
    const url = this.extractUrlFromConfig(config);
    const data = (config as { data?: unknown }).data;
    
    return `${method}:${url}:${JSON.stringify(data || {})}`;
  }

  private extractUrlFromConfig(config: EnhancedRequestConfig): string {
    return config.url || '/unknown';
  }

  private extractMethodFromConfig(config: EnhancedRequestConfig): string {
    return config.method?.toUpperCase() || 'GET';
  }

  private getUserIdFromToken(): string | null {
    try {
      const token = localStorage.getItem('mep_token');
      if (!token) return null;
      
      const parts = token.split('.');
      if (parts.length !== 3 || !parts[1]) return null;

      const payload = JSON.parse(atob(parts[1])) as { sub?: string; userId?: string };
      return payload.sub || payload.userId || null;
    } catch {
      return null;
    }
  }

  private setupDefaultInterceptors(): void {
    // Performance monitoring interceptor
    this.addInterceptor({
      name: 'performance-monitor',
      priority: 100,
      onRequest: async (config) => {
        (config as EnhancedRequestConfig & { metadata?: { startTime: number } }).metadata = { startTime: Date.now() };
        return config;
      },
      onResponse: async (response) => {
        const configWithMetadata = response.config as AxiosRequestConfig & { metadata?: { startTime: number } };
        const startTime = configWithMetadata.metadata?.startTime;
        if (startTime) {
          const duration = Date.now() - startTime;
          console.debug(`Request completed in ${duration}ms: ${response.config.method?.toUpperCase()} ${response.config.url}`);
        }
        return response;
      },
    });

    // Cache invalidation interceptor
    this.addInterceptor({
      name: 'cache-invalidation',
      priority: 90,
      onResponse: async (response) => {
        const method = response.config.method?.toUpperCase();
        
        // Invalidate caches for write operations
        if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method || '')) {
          const url = response.config.url || '';
          await this.invalidateRelatedCaches(url);
        }
        
        return response;
      },
    });
  }

  private async invalidateRelatedCaches(url: string): Promise<void> {
    const patterns: string[] = [];
    
    // Extract resource type from URL
    if (url.includes('/users')) {
      patterns.push('GET:/users');
      patterns.push('users');
    }
    
    if (url.includes('/establishments')) {
      patterns.push('GET:/establishments');
      patterns.push('establishments');
    }
    
    if (url.includes('/maintenance')) {
      patterns.push('maintenance');
    }
    
    // Invalidate related caches
    for (const pattern of patterns) {
      await this.cacheManager.invalidate(pattern);
    }
  }
}

// Export singleton instance
export const enhancedApiClient = new EnhancedApiClient();

// Export default client
export default enhancedApiClient;